import React, { useCallback, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { connect } from 'react-redux'
import { arrayToObject, EmptyArray, groupBy } from 'lib/array/arrayUtils'
import {
  EXP_CATEGORY_LUX_PLUS_ID,
  EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID,
} from 'constants/experience'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import CheckboxInputListItem from 'LuxKit/List/CheckboxInputListItem'
import FilterPanelCheckboxGroup, {
  type FilterPanelCheckItem,
} from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import useTrackExperienceSearchFilter from 'components/Experiences/hooks/useTrackExperienceSearchFilter'
import getIsExperienceLoyaltyBurnEnabled from 'loyalty/selectors/getIsExperienceLoyaltyBurnEnabled'
import useSearchBurnEnabled from 'hooks/useSearchBurnEnabled'
import { useAppSelector } from 'hooks/reduxHooks'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import useMostPopulatedSearchExperienceFacets from '../hooks/useMostPopulatedSearchExperienceFacets'
import usePrefetchExperienceSearchBurnCalculations from '../hooks/usePrefetchExperienceSearchBurnCalculations'

const LuxPlusMemberPriceLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
  color: ${(props) => props.theme.palette.product.luxPlus.altForeground};
`

const ListContainer = styled.div`
  margin-inline: ${rem(-16)};
  margin-bottom: ${rem(-8)};
`

interface MappedProps {
  categories: Array<App.ExperienceItemCategory>;
}

interface Props {
  place?: App.Place;
  listIds?: Array<string>;
  discoveryRevampEnabled?: boolean;
  compact?: boolean;
  isLuxPlusDiscountedExperiencesEnabled?: boolean;
}

interface ViewPriceInPointsOptionProps {
  listIds?: Array<string>;
}

function ViewPriceInPointsOption({ listIds }: ViewPriceInPointsOptionProps) {
  const intl = useIntl()
  const isLoyaltyEnabled = useAppSelector(getIsExperienceLoyaltyBurnEnabled)
  const { searchFilters, setSearchFilters } = useExperiencesSearchFilters()
  const trackSearchFilter = useTrackExperienceSearchFilter()
  const { setSearchBurnEnabled, setPointsToBurn } = useSearchBurnEnabled()
  usePrefetchExperienceSearchBurnCalculations(listIds)

  const viewPriceInPoints = !!searchFilters.viewPriceInPoints
  const viewPriceInPointsLabel = intl.formatMessage({
    id: 'Pl7ew0Wylbx4ZUv7DdgF',
    defaultMessage: 'View Price in Points',
    description: 'Checkbox label to show experience search prices as Société points only',
  })

  const item = useMemo<FilterPanelCheckItem>(() => ({
    value: 'view-price-in-points',
    checked: viewPriceInPoints,
    label: viewPriceInPointsLabel,
  }), [viewPriceInPoints, viewPriceInPointsLabel])

  const handleViewPriceInPointsToggle = useCallback(() => {
    trackSearchFilter({
      label: viewPriceInPointsLabel,
      type: 'view_price_in_points',
      value: viewPriceInPoints ? 'unselected' : 'selected',
    })

    if (viewPriceInPoints) {
      setSearchFilters({ ...searchFilters, viewPriceInPoints: undefined })
    } else {
      setSearchBurnEnabled(false)
      setPointsToBurn(undefined)
      setSearchFilters({ ...searchFilters, viewPriceInPoints: true })
    }
  }, [
    viewPriceInPoints,
    searchFilters,
    setSearchFilters,
    setSearchBurnEnabled,
    setPointsToBurn,
    trackSearchFilter,
    viewPriceInPointsLabel,
  ])

  if (!isLoyaltyEnabled) return null

  return (
    <FilterPanelCheckboxGroup
      name="viewPriceInPoints"
      items={[item]}
      onChange={handleViewPriceInPointsToggle}
    />
  )
}

function ExperienceSearchOnlyAtLuxuryEscapesFilter({
  place,
  listIds,
  discoveryRevampEnabled = false,
  compact = false,
  categories = EmptyArray,
  isLuxPlusDiscountedExperiencesEnabled,
}: Props & MappedProps) {
  const intl = useIntl()
  const { searchFilters, setSearchFilters } = useExperiencesSearchFilters()
  const trackSearchFilter = useTrackExperienceSearchFilter()
  const {
    categories: categoriesFacets,
    leExclusiveCount,
    luxPlusMemberPriceCount,
  } = useMostPopulatedSearchExperienceFacets(place)

  const luxPlusItems = useMemo((): Array<{
    titleLabel: React.ReactNode;
    count: number;
    value: number;
    checked: boolean;
  }> => {
    if (!isLuxPlusDiscountedExperiencesEnabled) return []

    const childCategoriesByParent = groupBy(categories, (cat) => cat.parentId)
    const facetsById = arrayToObject(
      categoriesFacets,
      (facet) => facet.categoryId,
    )
    const luxPlusParent = categories.find(
      (cat) => cat.level === 'parent' && cat.id === EXP_CATEGORY_LUX_PLUS_ID,
    )

    if (!luxPlusParent) return []

    return (childCategoriesByParent.get(luxPlusParent.id) ?? []).map((cat) => {
      const count =
        cat.id === EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID
          ? luxPlusMemberPriceCount
          : facetsById[cat.id]?.count ?? 0
      const checked = !!searchFilters.luxPlusFeatures?.length
      const luxPlusLabel = discoveryRevampEnabled ? (
        <FormattedMessage
          id="xQYG89GSVPtzZj15gb0j"
          defaultMessage="Lux Plus"
          description="Label text for Lux Plus checkbox option in experience search filters"
        />
      ) : (
        <FormattedMessage
          id="tbedd0QfyCsU78aK5PLw"
          defaultMessage="Member price"
          description="Label text for LuxPlus+ member price checkbox option in experience search filters"
        />
      )
      let titleLabel: React.ReactNode = cat.name
      if (cat.id === EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID) {
        titleLabel = (
          <LuxPlusMemberPriceLabel>
            <LuxPlusLogo height={10} />
            {luxPlusLabel}
          </LuxPlusMemberPriceLabel>
        )
      }
      return { titleLabel, count, value: cat.id, checked }
    })
  }, [
    categories,
    categoriesFacets,
    searchFilters.luxPlusFeatures,
    luxPlusMemberPriceCount,
    discoveryRevampEnabled,
    isLuxPlusDiscountedExperiencesEnabled,
  ])

  const leExclusiveTitle = intl.formatMessage(
    discoveryRevampEnabled
      ? {
          id: '-PmTBOAd00zapB4PVD4L',
          defaultMessage: 'Promotions & Discounts',
          description:
            'Checkbox label for the Luxury Escapes promotions and discounts experience search filter',
        }
      : {
          id: 'TxlMMKgor5yZq-d8fA7x',
          defaultMessage: 'Lux Exclusive',
          description: 'Tour category name for Luxury Escapes exclusive tours',
        },
  )

  const onLuxPlusMemberPriceChange = useCallback(() => {
    const selected = searchFilters.luxPlusFeatures?.includes('Member prices')

    trackSearchFilter({
      label: discoveryRevampEnabled ? 'Lux Plus' : 'Member price',
      type: 'lux_plus',
      value: selected ? 'unselected' : 'selected',
    })
    setSearchFilters({
      ...searchFilters,
      luxPlusFeatures: selected
        ? undefined
        : ['Member prices'],
    })
  }, [
    discoveryRevampEnabled,
    searchFilters,
    setSearchFilters,
    trackSearchFilter,
  ])

  const onLeExclusiveChange = useCallback(() => {
    trackSearchFilter({
      label: leExclusiveTitle,
      type: 'le_exclusive',
      value: searchFilters.leExclusive ? 'unselected' : 'selected',
    })
    setSearchFilters({
      ...searchFilters,
      leExclusive: !searchFilters.leExclusive,
    })
  }, [
    leExclusiveTitle,
    searchFilters,
    setSearchFilters,
    trackSearchFilter,
  ])

  const leExclusiveItem = (
    <CheckboxInputListItem
      inputPlacement="start"
      checked={!!searchFilters.leExclusive}
      onChange={onLeExclusiveChange}
      title={leExclusiveTitle}
      size="medium"
      disabled={leExclusiveCount === 0}
    />
  )

  const compactItems = useMemo<Array<FilterPanelCheckItem>>(() => {
    const leExclusiveLabel = discoveryRevampEnabled
      ? intl.formatMessage({
          id: '-PmTBOAd00zapB4PVD4L',
          defaultMessage: 'Promotions & Discounts',
          description:
            'Checkbox label for the Luxury Escapes promotions and discounts experience search filter',
        })
      : intl.formatMessage({
          id: 'TxlMMKgor5yZq-d8fA7x',
          defaultMessage: 'Lux Exclusive',
          description: 'Tour category name for Luxury Escapes exclusive tours',
        })

    return [
      {
        value: 'le-exclusive',
        label: leExclusiveLabel,
        checked: !!searchFilters.leExclusive,
        count: leExclusiveCount,
        hideCount: discoveryRevampEnabled,
        disabled: leExclusiveCount === 0,
      },
      ...luxPlusItems.map((item) => ({
        value: String(item.value),
        label: item.titleLabel,
        checked: item.checked,
        count: item.count,
        hideCount: discoveryRevampEnabled,
        disabled: item.count === 0,
      })),
    ]
  }, [
    discoveryRevampEnabled,
    intl,
    leExclusiveCount,
    luxPlusItems,
    searchFilters.leExclusive,
  ])

  const onCompactChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === 'le-exclusive') {
      onLeExclusiveChange()
      return
    }

    onLuxPlusMemberPriceChange()
  }, [onLeExclusiveChange, onLuxPlusMemberPriceChange])

  if (compact || discoveryRevampEnabled) {
    return (
      <>
        <FilterPanelCheckboxGroup
          name="onlyAtLuxuryEscapes"
          items={compactItems}
          onChange={onCompactChange}
        />
        {discoveryRevampEnabled && <ViewPriceInPointsOption listIds={listIds} />}
      </>
    )
  }

  return (
    <ListContainer>
      {luxPlusItems.map((item) => (
        <CheckboxInputListItem
          key={item.value}
          inputPlacement="start"
          checked={item.checked}
          onChange={onLuxPlusMemberPriceChange}
          title={item.titleLabel}
          size="medium"
          disabled={item.count === 0}
        />
      ))}
      {leExclusiveItem}
    </ListContainer>
  )
}

function mapStateToProps(state: App.State) {
  return {
    categories: state.experience.experienceCategories,
    isLuxPlusDiscountedExperiencesEnabled: getIsEnabledLuxPlusDiscountedExperiences(state),
  }
}

export default connect(mapStateToProps)(
  ExperienceSearchOnlyAtLuxuryEscapesFilter,
)
